import {DataSource} from './data-source';
import {clone, remove, uniq} from 'lodash';
import {Loan} from '../../model/loan.model';
import {MatSort} from '@angular/material/sort';
import {ArrayUtil} from '../../utils/array.util';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {MatPaginator} from '@angular/material/paginator';
import {ConfigService} from '../../service/config.service';
import {ApiServiceService} from '../../api-service.service';
import {XirrCalculator} from '../../service/xirr-calculator';
import {PortfolioType} from '../../model/portfolio-type.model';
import {PortfolioService} from '../../service/portfolio.service';
import {AfterViewInit, Component, ViewChild} from '@angular/core';
import {Keys, StorageService} from '../../service/storage.service';
import {GaugedInvestment} from '../../model/gauged-investment.model';
import {SelectedInvestments} from '../../model/selected-investments.model';
import {ManualinvestdetailComponent} from '../manualinvestdetail/manualinvestdetail.component';
import {ManualInvestPortfolioCreationData} from '../../model/portfolio-creation-data/manual-invest-portfolio-creation-data.model';

@Component({
    providers: [ApiServiceService],
    templateUrl: './manualinvest.component.html',
    styleUrls: ['../../../assets/styles/finzy-styles.scss', './manualinvest.component.scss']
})
export class ManualInvestComponent implements AfterViewInit {
    public selectedRatingToFilter = '';
    public selectedLocationToFilter = '';
    public lender50KLimitErrorMessage: string;
    public selectedFilledPercentageToFilter = '';
    public lender50kLimitDeclarationCheck = false;
    public selectedRatingRangeToFilter: string[] = [];

    @ViewChild(MatSort)
    public readonly sort: MatSort;
    @ViewChild(MatPaginator)
    public readonly paginator: MatPaginator;

    public readonly ratingOptions: string[] = [];
    public readonly locationOptions: string[] = [];
    public readonly dataSource: DataSource = new DataSource();
    public readonly selectedInvestments: SelectedInvestments = SelectedInvestments.empty();
    public readonly displayedColumns: any = ['roi', 'city', 'rating', 'loan_amount', 'filled_percentage', 'loan_reason', 'loan_details', 'fund_allocation'];
    public readonly filledOptions = [
        {name: 'Select All', value: ''},
        {name: 'Less than 40%', value: '0-40'},
        {name: '40 - 50 %', value: '40-50'},
        {name: '50 - 60 %', value: '50-60'},
        {name: '60 - 70 %', value: '60-70'},
        {name: '70% & above', value: '70-100'},
    ];
    public xirr: number;
    private readonly router: Router;
    private readonly matDialog: MatDialog;
    private readonly configService: ConfigService;
    private readonly storageService: StorageService;
    private readonly activatedRoute: ActivatedRoute;
    private readonly portfolioService: PortfolioService;
    private portfolioName;
    private potentialInvestments: GaugedInvestment[];
    private portfolioCreationData: ManualInvestPortfolioCreationData;
    private xirrCalculator: XirrCalculator;

    constructor(matDialog: MatDialog, activatedRoute: ActivatedRoute, storageService: StorageService,
                configService: ConfigService, router: Router, portfolioService: PortfolioService, xirrCalculator: XirrCalculator) {
        this.router = router;
        this.matDialog = matDialog;
        this.configService = configService;
        this.storageService = storageService;
        this.xirrCalculator = xirrCalculator;
        this.activatedRoute = activatedRoute;
        this.portfolioService = portfolioService;
        this.activatedRoute.data.subscribe(({investments, portfolioCreationData}: {
            investments: { potentialInvestments: GaugedInvestment[], selectedInvestments: SelectedInvestments },
            portfolioCreationData: ManualInvestPortfolioCreationData
        }) => {
            this.onPotentialInvestmentsReceived(investments.potentialInvestments);
            this.onSelectedInvestmentsReceived(investments.selectedInvestments);
            this.onPortfolioCreationDataReceived(portfolioCreationData);
        });
    }

    public openDialog(loan) {
        this.matDialog.open(ManualinvestdetailComponent, {data: loan, panelClass: 'manual-invest-details-dialog'});
    }

    public areLoansAvailable() {
        return this.dataSource.data.length > 0;
    }

    public getInvestmentAmount(): number {
        return this.portfolioCreationData.portfolioAmount;
    }

    public isValidInvestmentAmount(amountToBeInvested: number, loanId: string): boolean {
        return (amountToBeInvested % this.configService.getDenomination() === 0) && (amountToBeInvested > 0) &&
            amountToBeInvested <= this.getMaximumInvestmentAmount(loanId);
    }

    public getMaximumInvestmentAmount(loanId: string): number {
        const maximumInvestmentAllowedInThisLoan = this.potentialInvestments.find(gaugedInvestment => gaugedInvestment.loan._id === loanId).amount;
        const amountRemainingToBeAllocated = this.getInvestmentAmount() - this.selectedInvestments.allocatedAmount();
        return Math.min(maximumInvestmentAllowedInThisLoan, amountRemainingToBeAllocated);
    }

    public onPotentialInvestmentsReceived(potentialInvestments: GaugedInvestment[]): void {
        this.potentialInvestments = potentialInvestments;
        this.dataSource.data = potentialInvestments.map(i => i.loan);
        ArrayUtil.clear(this.locationOptions);
        uniq(potentialInvestments.map(i => i.loan.correspondence_address.city)).sort()
            .forEach(city => this.locationOptions.push(city));

        ArrayUtil.clear(this.ratingOptions);
        uniq(potentialInvestments.map(i => i.loan.rating)).sort()
            .forEach(city => this.ratingOptions.push(city));
    }

    investLater() {
        this.router.navigate(['/investordashboard']);
    }

    async addLoanToPortfolio(loan: Loan, amount: number): Promise<void> {
        const selectedGaugedInvestment = this.potentialInvestments.find(investment => loan._id === investment.loan._id);
        this.selectedInvestments.push({gaugedInvestment: selectedGaugedInvestment, amount: amount});
        await this.recalculateXirr(this.selectedInvestments);
    }

    async removeLoanFromPortfolio(loan: Loan): Promise<void> {
        remove(this.selectedInvestments, investment => investment.gaugedInvestment.loan._id === loan._id);
        await this.recalculateXirr(this.selectedInvestments);
    }

    async onLoanAddedOrRemoved(loan: Loan, amount: number, wasItAdded: boolean): Promise<void> {
        if (wasItAdded) {
            await this.addLoanToPortfolio(loan, amount);
        } else {
            await this.removeLoanFromPortfolio(loan);
        }
    }

    isLoanSelected(loan: Loan): boolean {
        return !!this.selectedInvestments.find(investment => investment.gaugedInvestment.loan._id === loan._id);
    }

    isFullInvestmentAmountAllocated(): boolean {
        return this.selectedInvestments.allocatedAmount() === this.getInvestmentAmount();
    }

    async returnToDashboard(): Promise<void> {
        await this.router.navigate(['/investordashboard']);
    }

    async proceedToNextStep(): Promise<void> {
        const portfolioType = this.getPortfolioType(this.selectedInvestments);

        if (this.areLoansAvailable()) {
            if (!this.lender50kLimitDeclarationCheck) {
                this.showErrorFor50KLimit('Please confirm the declaration by clicking on the checkbox above');
                return;
            } else {
                this.lender50KLimitErrorMessage = undefined;
                this.storageService.saveObjectToSession(Keys.LENDER_50K_LIMIT_CHECK, this.lender50kLimitDeclarationCheck);
            }
        }

        const manualInvestPortfolioCreationData = this.portfolioCreationData;
        manualInvestPortfolioCreationData.portfolioType = portfolioType;
        manualInvestPortfolioCreationData.portfolioName = this.portfolioName;
        manualInvestPortfolioCreationData.selectedInvestments = this.selectedInvestments;
        delete manualInvestPortfolioCreationData.portfolioPreference;

        await this.portfolioService.createOrUpdateLocalPortfolioCreationData(manualInvestPortfolioCreationData);

        const routingUrl = portfolioType === PortfolioType.ManualInvest ? './portfolio-summary' : './portfolio-preference';
        await this.router.navigate([routingUrl], {relativeTo: this.activatedRoute});
    }

    getPortfolioType(selectedInvestments) {
        if (!this.areLoansAvailable()) {
            return PortfolioType.HybridInvest;
        } else {
            return selectedInvestments.allocatedAmount() === this.getInvestmentAmount() ? PortfolioType.ManualInvest : PortfolioType.HybridInvest;
        }
    }

    changeRisk(risk, isRiskSelected: boolean): void {
        const existingFilteredRisks = this.selectedRatingRangeToFilter;
        const length = (risk === 'A+') ? 3 : 6;
        if (isRiskSelected) {
            const ratingsToBeAddedForFiltering = Array.from({length: length}, (v, k) => risk + (k + 1));
            ratingsToBeAddedForFiltering.forEach(rating => existingFilteredRisks.push(rating));
        } else {
            const ratingsToBeRemovedForFiltering = Array.from({length: length}, (v, k) => risk + (k + 1));
            ratingsToBeRemovedForFiltering.forEach(rating => remove(existingFilteredRisks, value => value === rating));
        }

        this.selectedRatingRangeToFilter = clone(this.selectedRatingRangeToFilter);
    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    public getSelectedAmountForLoan(loanId: string): number {
        const amountInvestedInLoan = this.selectedInvestments.findAmountInvestedInLoan(loanId);
        return amountInvestedInLoan ? amountInvestedInLoan : undefined;
    }

    onTitleChanged(title: string) {
        this.portfolioName = title;
    }

    public onPortfolioCreationDataReceived(portfolioCreationData: ManualInvestPortfolioCreationData): void {
        this.portfolioCreationData = portfolioCreationData;
    }

    async recalculateXirr(selectedInvestments: SelectedInvestments): Promise<void> {
        this.xirr = await this.xirrCalculator.calculateForInvestments(selectedInvestments.map(selectedInvestment => {
            return {
                investmentAmount: selectedInvestment.amount,
                roi: selectedInvestment.gaugedInvestment.loan.roi,
                tenure: selectedInvestment.gaugedInvestment.loan.loan_tenure
            };
        }));
    }

    public async onSelectedInvestmentsReceived(selectedInvestments: SelectedInvestments): Promise<void> {
        ArrayUtil.pushAll(this.selectedInvestments, selectedInvestments);
        this.recalculateXirr(this.selectedInvestments);
    }

    private showErrorFor50KLimit(errorMessage: string) {
        this.lender50KLimitErrorMessage = errorMessage;
    }
}
